import * as store from "../state/store.js";

// The renderer needs to know the current canvas size to fit / zoom / pan. The
// canvas-view component publishes it here whenever it resizes.
let canvasWidth = 0;
let canvasHeight = 0;

export function setCanvasSize(width: number, height: number): void {
  canvasWidth = width;
  canvasHeight = height;
  store.write.bumpView();
}

export function canvasSize(): { width: number; height: number } {
  return { width: canvasWidth, height: canvasHeight };
}

// Center + zoom the current drawing to fit the canvas viewport, with padding.
//
// Deriving pan: worldToScreen is
//   sx = x * zoom + pan.x
//   sy = -y * zoom + pan.y + h/2                (Y is flipped)
// We want the world center (cx, cy) to land at screen center (w/2, h/2):
//   w/2 = cx * zoom + pan.x        =>  pan.x = w/2 - cx * zoom
//   h/2 = -cy * zoom + pan.y + h/2 =>  pan.y = cy * zoom
export function fitToBounds(): void {
  const b = store.bounds();
  if (!b) return;

  // Load can race the initial ResizeObserver callback that publishes the
  // canvas size. If we don't have it yet, defer one frame and try again.
  if (canvasWidth === 0 || canvasHeight === 0) {
    requestAnimationFrame(() => fitToBounds());
    return;
  }

  const w = b.maxX - b.minX || 1;
  const h = b.maxY - b.minY || 1;
  const pad = 40;
  const scaleX = (canvasWidth - pad * 2) / w;
  const scaleY = (canvasHeight - pad * 2) / h;
  // No arbitrary upper cap — for small drawings (say 6-inch part on an 800px
  // canvas) the natural scale is ~120x, and capping at 10 would leave the
  // drawing at ~7% of the canvas. Users can always zoom out with the wheel.
  const zoom = Math.min(scaleX, scaleY);

  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  const panX = canvasWidth / 2 - cx * zoom;
  const panY = cy * zoom;

  store.batch(() => {
    store.write.zoom(zoom);
    // Fit-to-window defines "100%" for the current file — the status bar's
    // displayed percentage is relative to this.
    store.write.referenceZoom(zoom);
    store.write.pan({ x: panX, y: panY });
    store.write.bumpView();
  });
}

// Put world (0, 0) at the canvas center at the current zoom. Used on initial
// mount before any DXF is loaded so the origin — and the ruler / grid ticks
// around it — start in a sensible place.
export function centerOrigin(): void {
  if (canvasWidth === 0 || canvasHeight === 0) return;
  store.batch(() => {
    store.write.pan({ x: canvasWidth / 2, y: 0 });
    store.write.bumpView();
  });
}

// Re-center the loaded model horizontally in the current canvas without
// changing zoom or vertical pan. Called after the layer panel toggles so the
// drawing stays visually anchored in the middle of the visible canvas area.
export function centerHorizontally(): void {
  const b = store.bounds();
  if (!b) return;
  if (canvasWidth === 0) return;
  const zoom = store.zoom();
  const cx = (b.minX + b.maxX) / 2;
  const panX = canvasWidth / 2 - cx * zoom;
  const p = store.pan();
  store.batch(() => {
    store.write.pan({ x: panX, y: p.y });
    store.write.bumpView();
  });
}

// Pan by a delta in screen pixels.
export function panBy(dx: number, dy: number): void {
  const p = store.pan();
  store.batch(() => {
    store.write.pan({ x: p.x + dx, y: p.y + dy });
    store.write.bumpView();
  });
}

// Zoom around a screen-space anchor (usually the mouse cursor). Keeps the
// world point that was under the anchor before the zoom under the same
// screen anchor after the zoom.
//
// The 10000 upper bound is a defensive guard against runaway wheel input, not
// a UX limit; the actual initial zoom is set by fitToBounds and can already
// exceed 100 for tiny drawings.
//
// Y has an asymmetric convention in this codebase — worldToScreen defines
// `sy = -y * zoom + pan.y + height / 2` (DXF +Y is up; canvas +Y is down;
// world origin lands at the vertical middle of the canvas by default). That
// `+ height / 2` term means the "effective" screen anchor for the pan-Y
// formula is `anchorY - height / 2`, not `anchorY` itself. Using anchorY
// directly (the older revision of this function did) drifts pan.y by
// `(height/2) * (1 - factor)` per zoom step — ~30 px per wheel tick on a
// 600 px canvas, which surfaces as the model steadily marching off-screen
// during a zoom-out. X has no offset (world X=0 lands at pan.x), so its
// formula is anchor-based as-is.
export function zoomAt(anchorX: number, anchorY: number, factor: number): void {
  const oldZoom = store.zoom();
  const newZoom = Math.max(0.001, Math.min(10000, oldZoom * factor));
  const actual = newZoom / oldZoom;
  const p = store.pan();
  const anchorYOffset = anchorY - canvasHeight / 2;
  store.batch(() => {
    store.write.zoom(newZoom);
    store.write.pan({
      x: anchorX - (anchorX - p.x) * actual,
      y: anchorYOffset - (anchorYOffset - p.y) * actual,
    });
    store.write.bumpView();
  });
}
